import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Form,
  FormGroup,
  FormLabel,
  FormControl,
  Button,
  Image,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import config from '../config/config';
import { useAuth } from '../contexts/AuthContext';
import { useResto } from '../contexts/RestoContext';
import {
  setFlashMessage,
  useWebsite
} from '../contexts/WebsiteContext';
import { ucfirst } from '../utils/utils';

const PressAddItem = ({ handleAddItem }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { restoData } = useResto();
  const { setIsLoading } = useWebsite();

  const availableLanguages =
    restoData?.availableLanguages || [];

  /* ================= INITIAL FORM ================= */

  const defaultFormData = useMemo(() => ({
    website: restoData?.website || '',
    descriptionFr: '',
    descriptionEn: '',
    descriptionDe: '',
    descriptionEs: '',
    label: '',
    url: '',
  }), [restoData?.website]);

  const [formData, setFormData] =
    useState(defaultFormData);

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value || ''
    }));
  };

  const resetForm = () => {
    setFormData(defaultFormData);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token || !restoData?.website) return;

    setIsLoading(true);

    try {
      await axios.post(
        `${config.apiUrl}press`,
        {
          ...formData,
          website: restoData.website
        },
        {
          headers: {
            Authorization:
              `Bearer ${token}`
          }
        }
      );

      handleAddItem?.();
      resetForm();

      setFlashMessage(
        t('SUCCESS_CREATED_MESSAGE'),
        'success'
      );

    } catch {
      setFlashMessage(
        t('ERROR_MESSAGE'),
        'error'
      );
    }

    setIsLoading(false);
  };

  /* ================= RENDER ================= */

  return (
    <div className="p-3">

      <Form onSubmit={handleSubmit}>

        {/* TITLE */}
        <Row className="margin-top-20">
          <Col>
            <FormGroup>
              <FormLabel htmlFor="label">
                {t('TITLE')}
              </FormLabel>

              <FormControl
                type="text"
                id="label"
                name="label"
                value={formData.label}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </Col>
        </Row>

        {/* URL */}
        <Row className="margin-top-20">
          <Col>
            <FormGroup>
              <FormLabel htmlFor="url">
                {t('URL')}
              </FormLabel>

              <FormControl
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="https://example.com"
                required
              />
            </FormGroup>
          </Col>
        </Row>

        {/* DESCRIPTIONS */}
        <Row className="margin-top-20">
          {availableLanguages.map(language => {
            const key =
              `description${ucfirst(language)}`;

            return (
              <Col key={language}>
                <FormGroup>

                  <FormLabel htmlFor={key}>
                    {t('DESCRIPTION')}{' '}
                    <Image
                      src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                      className="flag-label"
                      alt={language}
                    />
                  </FormLabel>

                  <FormControl
                    as="textarea"
                    rows={3}
                    id={key}
                    name={key}
                    value={formData[key] || ''}
                    onChange={handleChange}
                  />

                </FormGroup>
              </Col>
            );
          })}
        </Row>

        {/* SUBMIT */}
        <div
          className="text-center margin-top-20"
          style={{ marginBottom: 60 }}
        >
          <Button type="submit">
            {t('ADD')}
          </Button>
        </div>

      </Form>

    </div>
  );
};

export default PressAddItem;